// Central API client for the admin panels (Shop Manager + Webmaster).
// Token is stored in localStorage and sent as a Bearer header.

// The Node API is hosted on the subdomain. Override with REACT_APP_API_BASE_URL
// if it ever moves.
const BASE = process.env.REACT_APP_API_BASE_URL || 'https://jgdetailing.zyrixcode.com';

const TOKEN_KEY = 'jg_admin_token';
const ROLE_KEY = 'jg_admin_role';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRole = () => localStorage.getItem(ROLE_KEY);
export const isAuthed = () => !!getToken();
export const logout = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ROLE_KEY);
};

const request = async (path, { method = 'GET', body } = {}) => {
  const token = getToken();
  // Cache-bust GETs so mutations (confirm/cancel/edit) reflect immediately.
  // The API sits behind Apache with ETags; without this a reload can be served
  // a stale list and it looks like the action "didn't work".
  let url = `${BASE}/api/admin${path}`;
  if (method === 'GET') url += `${url.includes('?') ? '&' : '?'}_=${Date.now()}`;
  const res = await fetch(url, {
    method,
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    logout();
    throw new Error('Session expired. Please log in again.');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
};

// Single-field login: backend auto-detects manager vs webmaster and returns the role.
// Upload a raw .zip file (deploy endpoints). Bypasses the JSON wrapper.
const uploadZip = async (path, file) => {
  const token = getToken();
  const res = await fetch(`${BASE}/api/admin${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/zip', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: file,
  });
  if (res.status === 401) { logout(); throw new Error('Session expired. Please log in again.'); }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
};

export const login = async (email, password) => {
  const data = await request('/login', { method: 'POST', body: { email, password } });
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(ROLE_KEY, data.role);
  return data;
};

// Ping public endpoints for the Webmaster latency widget (no auth needed).
export const pingEndpoint = async (path) => {
  const start = performance.now();
  try {
    const res = await fetch(`${BASE}${path}`, { method: 'GET' });
    return { path, ok: res.ok, status: res.status, ms: Math.round(performance.now() - start) };
  } catch (err) {
    return { path, ok: false, status: 0, ms: Math.round(performance.now() - start), error: err.message };
  }
};

export const api = {
  bookings: (params = {}) => request(`/bookings?${new URLSearchParams(params)}`),
  summary: () => request('/bookings/summary'),
  createBooking: (body) => request('/bookings', { method: 'POST', body }),
  updateBooking: (id, body) => request(`/bookings/${id}`, { method: 'PUT', body }),
  confirmBooking: (id) => request(`/bookings/${id}/confirm`, { method: 'POST' }),
  cancelBooking: (id, notify) => request(`/bookings/${id}/cancel`, { method: 'POST', body: { notify } }),
  purgeStalePending: () => request('/bookings/purge-stale-pending', { method: 'POST' }),
  health: () => request('/health'),
  logs: () => request('/logs'),

  // Diagnostics (webmaster only) — send a real test SMS / email / booking.
  testSms: (to) => request('/diag/test-sms', { method: 'POST', body: { to } }),
  testEmail: (to) => request('/diag/test-email', { method: 'POST', body: { to } }),
  testBooking: (to) => request('/diag/test-booking', { method: 'POST', body: { to } }),

  // Shop-manager accounts (webmaster only)
  managers: () => request('/managers'),
  inviteManager: (email) => request('/managers/invite', { method: 'POST', body: { email } }),
  removeManager: (id) => request(`/managers/${id}`, { method: 'DELETE' }),

  // Invite acceptance (public — no auth token needed)
  getInvite: (token) => request(`/invite/${token}`),
  acceptInvite: (token, password) => request(`/invite/${token}/accept`, { method: 'POST', body: { password } }),

  // Loyalty / rewards (punch card — earn by phone, no login)
  completeBooking: (id) => request(`/bookings/${id}/complete`, { method: 'POST' }),
  loyaltyMember: (phone) => request(`/loyalty/member?phone=${encodeURIComponent(phone)}`),
  loyaltyRedeem: (phone) => request('/loyalty/redeem', { method: 'POST', body: { phone } }),
  loyaltyConfig: () => request('/loyalty/config'),
  loyaltySetConfig: (cfg) => request('/loyalty/config', { method: 'POST', body: cfg }),
  loyaltyRewardsReady: () => request('/loyalty/rewards-ready'),

  // Marketing
  marketingAudience: () => request('/marketing/audience'),
  marketingCampaigns: () => request('/marketing/campaigns'),
  marketingSend: (message) => request('/marketing/send', { method: 'POST', body: { message } }),

  // Billing (prepaid wallet, Stripe)
  billing: () => request('/billing'),
  billingTransactions: () => request('/billing/transactions'),
  billingTopup: (amountCents, returnPath) => request('/billing/topup', { method: 'POST', body: { amount_cents: amountCents, returnPath } }),
  billingSetRate: (rateCents) => request('/billing/rate', { method: 'POST', body: { rate_cents: rateCents } }),

  // Self-deploy (webmaster only)
  deployInfo: () => request('/deploy/info'),
  saveDeployConfig: (paths) => request('/deploy/config', { method: 'POST', body: paths }),
  saveFtp: (cfg) => request('/deploy/ftp', { method: 'POST', body: cfg }),
  testFtp: () => request('/deploy/ftp/test', { method: 'POST' }),
  deployFrontend: (file) => uploadZip('/deploy/frontend', file),
  deployBackend: (file) => uploadZip('/deploy/backend', file),
};

// Build a shareable invite link for the current site origin.
export const inviteLink = (token) => `${window.location.origin}/portal/accept?token=${token}`;

export { BASE };
